import DataTable from "examples/Tables/DataTable";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import AlertDialog from "components/AlertDialog";
import RegionsDialog from "./RegionsDialog";
import { useState, useEffect, useMemo } from "react";
import MDBox from "components/MDBox";
import TableSearch from "components/TableSearch";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import AccessDenied from "components/AccessDenied";
import locationService from "services/location-service";
import { Link, Tooltip } from "@mui/material";
import { useLanguage } from "contexts/language.context";

export default function AllRegions({ permissions }) {
  const { label } = useLanguage();
  const [regions, setRegions] = useState(null);
  const [options, setOptions] = useState({ territories: null });
  const [currentRegion, setCurrentRegion] = useState(null);
  const [lastFilter, setLastFilter] = useState(null);

  const [rows, setRows] = useState([]);

  const [newDialogIsOpen, setNewDialogIsOpen] = useState(false);
  const [editDialogIsOpen, setEditDialogIsOpen] = useState(false);
  const [alertIsOpen, setAlertIsOpen] = useState(false);

  const columns = [
    {
      Header: label("name-singular"),
      accessor: "description",
      width: "100%",
    },
  ];

  if (permissions.update || permissions.delete) {
    columns.push({
      Header: "",
      accessor: "actions",
      align: "right",
      width: "100%",
    });
  }

  useEffect(() => {
    updateRows();
  }, [regions]);

  useEffect(() => {
    handleGetOptions();
  }, []);

  useEffect(() => {}, [options]);

  async function handleGetOptions() {
    async function getTerritories() {
      const [response, data] = await locationService.getTerritories();
      if (response.ok) {
        setOptions((curr) => ({ ...curr, territories: data }));
      }
    }

    await Promise.all([getTerritories()]);
  }

  async function handleAddRegion(newElement) {
    const [response, data] = await locationService.createRegion(newElement);
    if (response.ok) {
      const [response, data] = await locationService.getRegions(lastFilter);
      if (response.ok) {
        setRegions(data);
      }

      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  async function handleUpdateRegion(newElement) {
    const [response, data] = await locationService.updateRegion(
      newElement.id,
      newElement
    );
    if (response.ok) {
      const [response, data] = await locationService.getRegions(lastFilter);
      if (response.ok) {
        setRegions(data);
      }
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  async function handleDeleteRegion(id) {
    const [response, data] = await locationService.removeRegion(id);
    if (response.ok) {
      const [response, data] = await locationService.getRegions(lastFilter);
      if (response.ok) {
        setRegions(data);
      }
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  function updateRows() {
    setRows(
      regions?.length > 0
        ? regions.map((region) => {
            return {
              description: (
                <MDTypography variant="inherit">
                  {region.description}
                </MDTypography>
              ),

              actions: (
                <MDBox
                  display="flex"
                  flexDirection="row"
                  justifyContent="end"
                  alignItems="center"
                >
                  {permissions.update && (
                    <Tooltip title={label("edit")}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditDialogIsOpen(Math.random());
                          setCurrentRegion(region);
                        }}
                      >
                        <Icon>edit</Icon>
                      </IconButton>
                    </Tooltip>
                  )}
                  {permissions.delete && (
                    <Tooltip title={label("Delete")}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setAlertIsOpen(Math.random());
                          setCurrentRegion(region);
                        }}
                      >
                        <Icon>delete</Icon>
                      </IconButton>
                    </Tooltip>
                  )}
                </MDBox>
              ),
            };
          })
        : regions?.length === 0
          ? [
              {
                name: label("no-regions-were-found"),
              },
            ]
          : [
              {
                name: label("loading"),
              },
            ]
    );
  }

  const currentTerritory = useMemo(() => {
    if (lastFilter?.territoryId && options.territories)
      return options.territories.find((t) => t.id == lastFilter.territoryId);
  }, [options.territories, lastFilter?.territoryId]);

  const filterBySelectsItems = useMemo(() => {
    return [
      {
        id: "territoryId",
        items: [
          { value: 0, render: label("all") },
          ...(options.territories?.map((territory) => {
            return {
              value: territory.id,
              render: territory.name,
            };
          }) || []),
        ],
        label: label("filter-by-territory-label"),
      },
    ];
  }, [options.territories]);

  return (
    <>
      <AlertDialog
        open={alertIsOpen}
        setOpen={setAlertIsOpen}
        title={label("delete-alert-title")}
        content={label("delete-alert-message")}
        onOk={async () => {
          await handleDeleteRegion(currentRegion.id);
        }}
      />
      <RegionsDialog
        isOpen={newDialogIsOpen}
        onSubmit={handleAddRegion}
        data={{
          region: {
            description: "",
            territoryId: currentTerritory?.id || null,
          },
          territories: options.territories,
        }}
      />
      <RegionsDialog
        type="edit"
        isOpen={editDialogIsOpen}
        onSubmit={handleUpdateRegion}
        data={{ region: currentRegion, territories: options.territories }}
      />

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  {currentTerritory ? `${currentTerritory.name}'s ` : " "}
                  {label("region-plural")}{" "}
                  {permissions.get ? `(${regions?.length || 0})` : ""}
                </MDTypography>
                {permissions.create && (
                  <MDButton
                    onClick={() => {
                      setNewDialogIsOpen(Math.random());
                    }}
                  >
                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                    &nbsp;{label("add-new-region-button")}
                  </MDButton>
                )}
              </MDBox>
              {permissions.get ? (
                <>
                  <MDBox p={2} mt={2}>
                    <TableSearch
                      id="all-regions"
                      internalLoading={!Boolean(regions)}
                      externalLoading={!Boolean(options.territories)}
                      fullWidth
                      setData={setRegions}
                      onSearch={async (request, callback) => {
                        setLastFilter(request);
                        callback(await locationService.getRegions(request));
                      }}
                      label={label("search-regions-label")}
                      filterBySelectsItems={filterBySelectsItems}
                    />
                  </MDBox>
                  <MDBox>
                    <DataTable
                      loading={!Boolean(regions)}
                      table={{ columns: columns, rows: rows }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                    />
                  </MDBox>
                </>
              ) : (
                <AccessDenied type="component" />
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </>
  );
}
